import {
  IonButton,
  IonButtons,
  IonContent,
  IonHeader,
  IonIcon,
  IonLabel,
  IonPage,
  IonRow,
  IonTitle,
  IonToolbar,
  useIonViewDidLeave,
  useIonViewWillEnter,
} from '@ionic/react'
import { addCircleOutline, chevronBackOutline } from 'ionicons/icons'
import React, { BaseSyntheticEvent, useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useHistory } from 'react-router'
import ButtonWallComponent from '../../../../components/buttonWall/ButtonWall.component'
import { TabConstants } from '../../../../components/tabs/Tabs.constants'
import { getQuickActionsSelector } from '../../selectors/quickActions.selector'
import './QuickActionButtons.page.css'
import { useTranslation } from 'react-i18next'
import useDeviceMetrics from '../../../../utilities/useDeviceMetrics'
import { resetState as resetMedicationAdministeredState } from '../../../addMedicationAdministered/store/addMedicationAdministered.actions'
import { resetState as resetIntubationState } from '../../../intubation/store/intubation.actions'
import { resetState as resetCardiacState } from '../../../procedures/cardiac/store/cardiac.actions'
import { resetState as resetAirwayState } from '../../../procedures/airway/store/airway.actions'
import { resetState as resetChildbirthOBState } from '../../../procedures/childbirthOB/store/childbirthOB.actions'
import { resetState as resetInitiateIVState } from '../../../procedures/initiateIv/store/initiateIv.actions'
import { resetState as resetImmobilizationState } from '../../../procedures/immobilization/store/immobilization.actions'
import { resetState as resetMedicalConsultState } from '../../../procedures/medicalConsult/store/medicalConsult.actions'
import { resetState as resetExtricationState } from '../../../procedures/extrication/store/extrication.actions'
import { resetState as resetHospitalNotifyState } from '../../../procedures/hospitalNotify/store/hospitalNotify.actions'
import { resetState as resetOperationsState } from '../../../procedures/operations/store/operations.actions'
import { resetState as resetRescueState } from '../../../procedures/rescue/store/rescue.actions'
import { resetState as resetVentilatorState } from '../../../procedures/ventilator/store/ventilator.actions'
import { resetState as resetWoundCareState } from '../../../procedures/woundCare/store/woundCare.actions'
import { resetState as resetDrainState } from '../../../procedures/drain/store/drain.actions'
import { resetState as resetBloodProductAdministration } from '../../../procedures/bloodProductAdministration/store/bloodProductAdministration.actions'
import { resetState as resetCriticalCareState } from '../../../procedures/criticalCare/store/criticalCare.actions'
import PCRHeader from '../../../../components/pcrHeader/PCRHeader.component'
import { getOrientation } from '../../../../utilities/getOrientation.util'
import { useWindowSize } from '../../../../hooks'
import { resetTitrateState } from '../../../procedures/titrate/store/titrate.actions'
import { resetLabsState } from '../../../procedures/labs/store/labs.actions'
import { setShowRepeatVitals, setRedirectedFrom, setAddVital } from '../../../vitals/store/vitals.actions'
import { resetState as resetVentricularAssistDevice } from '../../../procedures/ventricularAssistDevice/store/ventricularAssistDevice.actions'

const QuickActionButtons: React.FC = () => {
  const history = useHistory()
  const dispatch = useDispatch()
  const { t } = useTranslation()
  const { isTablet: isTabletDevice } = useDeviceMetrics()
  const quickActions = useSelector(getQuickActionsSelector)
  const { mobileQuickActionButtonRows, quickActionButtonRows, parentQuickActionLabel, filteredQuickActionButtons } =
    quickActions
  const { width, height } = useWindowSize({ debounceMs: 300 })
  const [orientation, setOrientation] = useState(getOrientation(width, height))

  const onBackClick = (e: BaseSyntheticEvent) => {
    e.preventDefault()
    history.push(TabConstants.QUICK_ACTIONS_TAB)
  }

  useEffect(() => {
    setOrientation(getOrientation(width, height))
  }, [width, height])

  const resetProcedures = () => {
    dispatch(resetMedicationAdministeredState())
    dispatch(resetIntubationState())
    dispatch(resetCardiacState())
    dispatch(resetAirwayState())
    dispatch(resetChildbirthOBState())
    dispatch(resetExtricationState())
    dispatch(resetHospitalNotifyState())
    dispatch(resetInitiateIVState())
    dispatch(resetImmobilizationState())
    dispatch(resetMedicalConsultState())
    dispatch(resetOperationsState())
    dispatch(resetRescueState())
    dispatch(resetVentilatorState())
    dispatch(resetWoundCareState())
    dispatch(resetDrainState())
    dispatch(resetTitrateState())
    dispatch(resetLabsState())
    dispatch(resetBloodProductAdministration())
    dispatch(resetVentricularAssistDevice())
    dispatch(resetCriticalCareState())
  }

  useIonViewWillEnter(() => {
    resetProcedures()
  })

  useIonViewDidLeave(() => {
    resetProcedures()
  })

  const onAddVitalsClick = (e: BaseSyntheticEvent) => {
    e.preventDefault()
    dispatch(setShowRepeatVitals(true))
    dispatch(setRedirectedFrom(TabConstants.QUICK_ACTIONS_TAB))
    dispatch(setAddVital())
    history.push(TabConstants.VITALS_TAB)
  }

  return (
    <IonPage>
      <PCRHeader />
      <IonHeader>
        <IonToolbar color='light'>
          <IonButtons slot='start'>
            <IonButton onClick={onBackClick} color='primary-alt'>
              <IonIcon icon={chevronBackOutline} />
              {t('quickActions.actions')}
            </IonButton>
          </IonButtons>
          <IonTitle>{parentQuickActionLabel}</IonTitle>
        </IonToolbar>
      </IonHeader>

      <IonContent fullscreen color='light'>
        <IonRow>
          <IonButton
            data-testid='AddVitals'
            className='mt-1 transform-none'
            fill='clear'
            color='primary-alt'
            onClick={onAddVitalsClick}>
            <IonIcon icon={addCircleOutline} />
            <IonLabel>{t('timeline.addVitals')}</IonLabel>
          </IonButton>
        </IonRow>
        {filteredQuickActionButtons && (
          <ButtonWallComponent
            rows={
              (isTabletDevice || orientation === 'landscape') && width > 770
                ? quickActionButtonRows
                : mobileQuickActionButtonRows
            }
          />
        )}
        {!filteredQuickActionButtons.length && <h3>{t('quickActions.emptyLabel')}</h3>}
      </IonContent>
    </IonPage>
  )
}

export default QuickActionButtons
